import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDates } from "../../../dates/DatesContext";
import { useQuery } from "@tanstack/react-query";
import { useProductByDate } from "../../../modules/perf_reports/api/product/useProductByDate";
import { useProductTotals } from "../../../modules/perf_reports/api/product/useProductTotals";
import useSelectedMetrics from "../merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../merchandise/MultiMetricChart";
import Loading from "../../core/blocks/Loading";
import getMetricSpec from "../../../modules/merchandising/utils/metrics";
import DetailedMetric from "../../core/blocks/DetailedMetric";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import moment from "moment";
import ExternalProductLink from "../merchandise/ExternalProductLink";
import generateLink from "../../../utils/generateLink";

export const ProductExpander = ({ product, chartStartDate, highlightDate = null }) => {
  const { t } = useTranslation(["merchandising"]);
  const acosOrRoas = useAcosOrRoas();

  const { id: productId, product_title: productTitle, asin, sku } = product ?? {};

  const { group } = useDates();

  const dateOverrides = chartStartDate ? { start: new Date(chartStartDate) } : {};

  const byDateQuery = useQuery(useProductByDate({ productId, ...dateOverrides }, { enabled: !!productId }));

  const totalsQuery = useQuery(useProductTotals({ productId, ...dateOverrides }, { enabled: !!productId }));

  const isLoading = byDateQuery.isLoading || totalsQuery.isLoading;

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["sales", "ad_spend", "page_views"],
    "productAnnotationExpander",
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("bsr"),
      getMetricSpec("page_views"),
      getMetricSpec("sales"),
      getMetricSpec("units"),
      getMetricSpec("ad_sales"),
      getMetricSpec("ad_spend"),
      acosOrRoas(getMetricSpec("acos"), getMetricSpec("roas")),
    ],
    [acosOrRoas],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const dateFilterWithoutToday =
    byDateQuery.data?.data?.filter((d) => moment().format("YYYY-MM-DD") !== d.start_date) ?? [];

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div className="bg-white">
      <Row className="pt-3">
        <Col xs={12} md={6} className="pb-4 px-5">
          <h3
            style={{
              fontSize: 22,
            }}
          >
            {productTitle}
          </h3>

          <span className="fs-standard">
            {asin} ({sku}) &nbsp;
            <ExternalProductLink asin={asin} />
          </span>
        </Col>
        <Col xs={12}>
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={dateFilterWithoutToday}
            group={group}
            handleLegendChange={handleLegendChange}
            showAnnotations={false}
            highlightDate={highlightDate}
          />
        </Col>
        <Col xs={12}>
          <Row className="pt-4">
            <Col xs={12} md={6} lg={2} className={"pe-2"}>
              <div className="">
                <div>
                  <DetailedMetric
                    center
                    metric={{
                      id: "product_price",
                      name: t(($) => $.merchandising["Product Price"]),
                      format: "currency",
                      decimalPlaces: 0,
                      accessor: (data) => data.value,
                    }}
                    data={{ value: product?.product_price }}
                    showTarget={false}
                  />
                </div>
              </div>
            </Col>
            {metrics?.map((m, i) => {
              return (
                <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                  <div className="">
                    <div>
                      <DetailedMetric center metric={m} data={totalsQuery.data?.data} showTarget={false} />
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
          <Row className="fs-standard pb-5">
            <Col xs={12} className="text-center">
              {generateLink(
                "listing",
                product,
                null,
                "both",
                "inherit",
                false,
                t(($) => $.merchandising["View Listing"]),
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};
